import { LoadingButton } from "@mui/lab";
import { Box, CircularProgress, Dialog, DialogContent } from "@mui/material";
import { Form, Formik } from "formik";
import { useState } from "react";
import CustomDialogTitle from "../../../components/Common/customDialogTitle";
import { API_RESULT, API_VERSION } from "../../../enums/common.enum";
import useAxiosInstance from "../../../hooks/useAxiosInstance";
import { updateBankDetailsSchema } from "../../../schemas/dunzoproviders.schema";
import { errorToast, handleError, successToast } from "../../../utils/common";
import BankDetailsView from "../SingleBorrower/Summary/components/BankDetailsView";
import { is } from "date-fns/locale";

const EditBankDetailsModal = ({
  showBankDetailsModal,
  setShowBankDetailsModal,
  borrowerInfo,
  setBorrowerInfo,
  selectedBank,
  setSelectedBank,
}: any) => {
  const [loading, setLoading] = useState(false);
  const privateInstance = useAxiosInstance();
  const isEdit = Boolean(selectedBank);

  const handleSubmit = async (values: any, actions: any) => {
    const updatedBank = {
      account_name: values.account_name,
      bank: values.bank,
      account_no: values.account_no,
      confirm_account_no: values.confirm_account_no,
      branch: values.branch,
      ifsc_code: values.ifsc_code,
      account_type: values.account_type,
      is_primary: values.is_primary,
    };

    let updatedBanks: any[] = [];

    if (selectedBank) {
      updatedBanks = borrowerInfo.bank_details.map((bank: any) =>
        bank.account_no === selectedBank.account_no
          ? { ...bank, ...updatedBank }
          : bank,
      );
    } else {
      updatedBanks = [...(borrowerInfo.bank_details || []), updatedBank];
    }

    if (updatedBank.is_primary) {
      updatedBanks = updatedBanks.map((bank: any) => ({
        ...bank,
        is_primary: bank.account_no === updatedBank.account_no,
      }));
    }

    const payload = {
      borrower_id: borrowerInfo?.borrower_id,
      bank_details: updatedBanks,
    };

    setLoading(true);

    try {
      const result = await privateInstance.post(
        `${API_VERSION.V3}/borrowers`,
        payload,
      );

      if (result?.data?.status === API_RESULT.SUCCESS) {
        setShowBankDetailsModal(false);

        const updatedBank =
          result.data.response?.bank_details?.[0] || payload.bank_details[0];

        setBorrowerInfo((prev: any) => ({
          ...prev,
          bank_details: updatedBanks,
        }));

        successToast(
          selectedBank
            ? "Bank details updated successfully"
            : "Bank added successfully",
        );
      } else {
        errorToast(result.data.message);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={showBankDetailsModal}
      onClose={() => setShowBankDetailsModal(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth="md"
    >
      <CustomDialogTitle
        id="customized-dialog-title"
        onClose={() => setShowBankDetailsModal(false)}
      >
        {isEdit ? "Update" : "Add"} Bank Details
      </CustomDialogTitle>
      <DialogContent sx={{ overflowY: "scroll" }}>
        <Formik
          initialValues={{
            account_name: selectedBank?.account_name || "",
            bank: selectedBank?.bank || "",
            account_no: selectedBank?.account_no || "",
            confirm_account_no: selectedBank?.confirm_account_no || "",
            branch: selectedBank?.branch || "",
            ifsc_code: selectedBank?.ifsc_code || "",
            account_type: selectedBank?.account_type || "",
            is_primary: selectedBank?.is_primary || "",
          }}
          validationSchema={updateBankDetailsSchema}
          onSubmit={(values: any, actions: any) => {
            actions.setSubmitting(false);
            handleSubmit(values, actions);
          }}
        >
          {(props) => {
            const { values, setFieldValue, handleBlur, errors, touched } =
              props;
            return (
              <Form>
                <BankDetailsView
                  values={values}
                  setFieldValue={setFieldValue}
                  handleBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />

                <Box textAlign="center" mt={3}>
                  <LoadingButton
                    size="medium"
                    color="primary"
                    loading={loading}
                    loadingIndicator={
                      <CircularProgress size={20} sx={{ color: "#fff" }} />
                    }
                    variant="contained"
                    type="submit"
                  >
                    {isEdit ? "Update" : "Add"}
                  </LoadingButton>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default EditBankDetailsModal;
